import React from 'react'
import PropTypes from 'prop-types'
import {Helmet} from 'react-helmet'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {ACTIVE_DATA_ENABLED} from '../../../constants'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'
import {useBrand} from '../../../contexts/brand-context'

const AppHelmet = ({normalizedLang, appConfig}) => {
    const {site} = useMultiSite()
    const siteId = site?.id
    const {isGarage} = useBrand()
    // Didomi consent SDK loader (injected async by @didomi/react → defaults to Low priority)
    const DIDOMI_PUBLIC_API_KEY = getSiteEnvVarValue(appConfig, 'DIDOMI_PUBLIC_API_KEY', siteId)
    const IS_DIDOMI_ENABLED = isGarage
        ? getSiteEnvVarValue(appConfig, 'IS_DIDOMI_ENABLED_GARAGE', siteId) === 'true'
        : getSiteEnvVarValue(appConfig, 'IS_DIDOMI_ENABLED_DYNAMITE', siteId) === 'true'
    const DIDOMI_NOTICE_ID = isGarage
        ? getSiteEnvVarValue(appConfig, 'DIDOMI_NOTICE_ID_GARAGE', siteId)
        : getSiteEnvVarValue(appConfig, 'DIDOMI_NOTICE_ID_DYNAMITE', siteId)
    const didomiLoaderUrl =
        IS_DIDOMI_ENABLED && DIDOMI_PUBLIC_API_KEY && DIDOMI_NOTICE_ID
            ? `https://sdk.privacy-center.org/${DIDOMI_PUBLIC_API_KEY}/loader.js?target_type=notice&target=${DIDOMI_NOTICE_ID}`
            : null

    return (
    <Helmet htmlAttributes={{lang: normalizedLang}}>
        {/* Preconnect to critical third-party origins */}
        <link rel="preconnect" href="https://edge.disstg.commercecloud.salesforce.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        {didomiLoaderUrl && <link rel="preconnect" href="https://sdk.privacy-center.org" />}
        <link rel="dns-prefetch" href="https://js.klarna.com" />
        <link rel="dns-prefetch" href="https://cdn.noibu.com" />
        {/* Preload critical fonts */}
        <link
            rel="preload"
            href={getAssetUrl('static/fonts/GarageNeue-Regular.woff2')}
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
        />
        <link
            rel="preload"
            href={getAssetUrl('static/fonts/GarageNeue-Medium.woff2')}
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
        />
        <link
            rel="preload"
            href={getAssetUrl('static/fonts/GarageNeue-Bold.woff2')}
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
        />
        {/* Preload Didomi loader at high priority (overrides the SDK's Low-priority async inject) */}
        {didomiLoaderUrl && (
            <link rel="preload" href={didomiLoaderUrl} as="script" fetchpriority="high" />
        )}
        {/* No GTM preload: GTMScript defers execution to load + idle, so preloading only pulled the
            download into the LCP window. The preconnect above still covers the later load. */}
        {ACTIVE_DATA_ENABLED && (
            <script
                src={getAssetUrl('static/head-active_data.js')}
                id="headActiveData"
                type="text/javascript"
            ></script>
        )}
    </Helmet>
    )
}
AppHelmet.propTypes = {
    normalizedLang: PropTypes.string.isRequired,
    appConfig: PropTypes.object.isRequired
}

export default AppHelmet
